import { FC, useEffect } from 'react'

import { cn } from '@bem-react/classname'

import { Footer } from '@/components/Layout/Footer'
import { Header } from '@/components/Layout/Header'
import { Tag } from '@/components/UI/Tag'

import './style.scss'

const cnPrivacy = cn('Policy')

export const Privacy: FC = (): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className={cnPrivacy()}>
      <Header isHome />
      <div className={cnPrivacy('wrapper')}>
        <Tag className={cnPrivacy('title')}>PRIVACY POLICY</Tag>
        <div className={cnPrivacy('info')}>Last updated January 31, 2025</div>
        <p className={cnPrivacy('text')}>
          This Privacy Notice for EXERCIAL, s.ro. ('we', 'us', or 'our'),
          describes how and why we might access, collect, store, use, and/or
          share ('process') your personal information when you use our services
          ('Services'), including when you:
        </p>
        <ul className={cnPrivacy('list')}>
          <li className={cnPrivacy('item')}>
            Visit our website at https://exstream.cc, or any website of ours
            that links to this Privacy Notice
          </li>
          <li className={cnPrivacy('item')}>
            Engage with us in other related ways, including any sales,
            marketing, or events
          </li>
        </ul>
        <p className={cnPrivacy('text')}>
          Questions or concerns? Reading this Privacy Notice will help you
          understand your privacy rights and choices. We are responsible for
          making decisions about how your personal information is processed. If
          you do not agree with our policies and practices, please do not use
          our Services.
        </p>
        <h2 className={cnPrivacy('subtitle')}>SUMMARY OF KEY POINTS</h2>
        <p className={cnPrivacy('text')}>
          This summary provides key points from our Privacy Notice, but you can
          find out more details about any of these topics by clicking the link
          following each key point or by using our table of contents below to
          find the section you are looking for. <br />
          What personal information do we process? When you visit, use, or
          navigate our Services, we may process personal information depending
          on how you interact with us and the Services, the choices you make,
          and the products and features you use. Learn more about personal
          information you disclose to us. <br />
          Do we process any sensitive personal information? Some of the
          information may be considered 'special' or 'sensitive' in certain
          jurisdictions, for example your racial or ethnic origins, sexual
          orientation, and religious beliefs. We do not process sensitive
          personal information. <br />
          Do we collect any information from third parties? We do not collect
          any information from third parties. <br />
          How do we process your information? We process your information to
          provide, improve, and administer our Services, communicate with you,
          for security and fraud prevention, and to comply with law. We may also
          process your information for other purposes with your consent. We
          process your information only when we have a valid legal reason to do
          so. Learn more about how we process your information. <br />
          In what situations and with which parties do we share personal
          information? We may share information in specific situations and with
          specific third parties. Learn more about when and with whom we share
          your personal information. <br />
        </p>
        <p className={cnPrivacy('text')}>
          How do we keep your information safe? We have adequate organisational
          and technical processes and procedures in place to protect your
          personal information. <br />
          However, no electronic transmission over the internet or information
          storage technology can be guaranteed to be 100% secure, so we cannot
          promise or guarantee that hackers, cybercriminals, or other
          unauthorised third parties will not be able to defeat our security and
          improperly collect, access, steal, or modify your information. Learn
          more about how we keep your information safe. <br />
          What are your rights? Depending on where you are located
          geographically, the applicable privacy law may mean you have certain
          rights regarding your personal information. Learn more about your
          privacy rights. <br />
          How do you exercise your rights? The easiest way to exercise your
          rights is by submitting a data subject access request, or by
          contacting us. We will consider and act upon any request in accordance
          with applicable data protection laws. <br />
          Want to learn more about what we do with any information we collect?
          Review the Privacy Notice in full.
        </p>
        <h2 className={cnPrivacy('subtitle')}>TABLE OF CONTENTS</h2>
        <ul className={cnPrivacy('list', { decimal: true })}>
          <li className={cnPrivacy('item')}>WHAT INFORMATION DO WE COLLECT?</li>
          <li className={cnPrivacy('item')}>
            HOW DO WE PROCESS YOUR INFORMATION?
          </li>
          <li className={cnPrivacy('item')}>
            WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
          </li>
          <li className={cnPrivacy('item')}>
            WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </li>
          <li className={cnPrivacy('item')}>
            DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
          </li>
          <li className={cnPrivacy('item')}>
            HOW LONG DO WE KEEP YOUR INFORMATION?
          </li>
          <li className={cnPrivacy('item')}>
            HOW DO WE KEEP YOUR INFORMATION SAFE?
          </li>
          <li className={cnPrivacy('item')}>
            DO WE COLLECT INFORMATION FROM MINORS?
          </li>
          <li className={cnPrivacy('item')}>WHAT ARE YOUR PRIVACY RIGHTS?</li>
          <li className={cnPrivacy('item')}>
            CONTROLS FOR DO-NOT-TRACK FEATURES
          </li>
          <li className={cnPrivacy('item')}>
            DO WE MAKE UPDATES TO THIS NOTICE?
          </li>
          <li className={cnPrivacy('item')}>
            HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
          </li>
          <li className={cnPrivacy('item')}>
            HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
          </li>
        </ul>
        <h2 className={cnPrivacy('subtitle')}>
          1. WHAT INFORMATION DO WE COLLECT?
        </h2>
        <p className={cnPrivacy('text')}>
          Personal information you disclose to us <br />
          In Short: We collect personal information that you provide to us.{' '}
          <br />
          We collect personal information that you voluntarily provide to us
          when you register on the Services, express an interest in obtaining
          information about us or our products and Services, when you
          participate in activities on the Services, or otherwise when you
          contact us. <br />
          Personal Information Provided by You. The personal information that we
          collect depends on the context of your interactions with us and the
          Services, the choices you make, and the products and features you use.
          The personal information we collect may include the following:
        </p>
        <ul className={cnPrivacy('list')}>
          <li className={cnPrivacy('item')}>names</li>
          <li className={cnPrivacy('item')}>phone numbers</li>
          <li className={cnPrivacy('item')}>contact or authentication data</li>
          <li className={cnPrivacy('item')}>billing addresses</li>
          <li className={cnPrivacy('item')}>debit/credit card numbers</li>
          <li className={cnPrivacy('item')}>usernames</li>
          <li className={cnPrivacy('item')}>passwords</li>
          <li className={cnPrivacy('item')}>email addresses</li>
        </ul>
        <p className={cnPrivacy('text')}>
          Sensitive Information. We do not process sensitive information. <br />
          Payment Data. We may collect data necessary to process your payment if
          you choose to make purchases, such as your payment instrument number,
          and the security code associated with your payment instrument. All
          payment data is handled and stored by <br />
          You may find their privacy notice link(s) here: <br />
          All personal information that you provide to us must be true,
          complete, and accurate, and you must notify us of any changes to such
          personal information.
        </p>
        <h2 className={cnPrivacy('subtitle')}>
          2. HOW DO WE PROCESS YOUR INFORMATION?
        </h2>
        <p className={cnPrivacy('text')}>
          In Short: We process your information to provide, improve, and
          administer our Services, communicate with you, for security and fraud
          prevention, and to comply with law. <br />
          We may also process your information for other purposes with your
          consent. <br />
          We process your personal information for a variety of reasons,
          depending on how you interact with our Services, including:
        </p>
        <ul className={cnPrivacy('list')}>
          <li className={cnPrivacy('item')}>
            To facilitate account creation and authentication and otherwise
            manage user accounts. We may process your information so you can
            create and log in to your account, as well as keep your account in
            working order.
          </li>
          <li className={cnPrivacy('item')}>
            To respond to user inquiries/offer support to users. We may process
            your information to respond to your inquiries and solve any
            potential issues you might have with the requested service.
          </li>
          <li className={cnPrivacy('item')}>
            To send administrative information to you. We may process your
            information to send you details about our products and services,
            changes to our terms and policies, and other similar information.
          </li>
          <li className={cnPrivacy('item')}>
            To fulfil and manage your orders. We may process your information to
            fulfil and manage your orders, payments, returns, and exchanges made
            through the Services.
          </li>
          <li className={cnPrivacy('item')}>
            To save or protect an individual's vital interest. We may process
            your information when necessary to save or protect an individual's
            vital interest, such as to prevent harm.
          </li>
        </ul>
        <h2 className={cnPrivacy('subtitle')}>
          3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
        </h2>
        <p className={cnPrivacy('text')}>
          In Short: We only process your personal information when we believe it
          is necessary and we have a valid legal reason (i.e. legal basis) to do
          so under applicable law, like with your consent, to comply with laws,
          to provide you with services to enter into or fulfil our contractual
          obligations, to protect your rights, or to fulfil our legitimate
          business interests. <br />
          The General Data Protection Regulation (GDPR) and UK GDPR require us
          to explain the valid legal bases we rely on in order to process your
          personal information. As such, we may rely on the following legal
          bases to process your personal information:
        </p>
        <ul className={cnPrivacy('list')}>
          <li className={cnPrivacy('item')}>
            Consent. We may process your information if you have given us
            permission (i.e. consent) to use your personal information for a
            specific purpose. You can withdraw your consent at any time. Learn
            more about withdrawing your consent.
          </li>
          <li className={cnPrivacy('item')}>
            Performance of a Contract. We may process your personal information
            when we believe it is necessary to fulfil our contractual
            obligations to you, including providing our Services or at your
            request prior to entering into a contract with you.
          </li>
          <li className={cnPrivacy('item')}>
            Legal Obligations. We may process your information where we believe
            it is necessary for compliance with our legal obligations, such as
            to cooperate with a law enforcement body or regulatory agency,
            exercise or defend our legal rights, or disclose your information as
            evidence in litigation in which we are involved.
          </li>
          <li className={cnPrivacy('item')}>
            Vital Interests. We may process your information where we believe it
            is necessary to protect your vital interests or the vital interests
            of a third party, such as situations involving potential threats to
            the safety of any person.
          </li>
        </ul>
        <h2 className={cnPrivacy('subtitle')}>
          4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
        </h2>
        <p className={cnPrivacy('text')}>
          In Short: We may share information in specific situations described in
          this section and/or with the following third parties. <br />
          We may need to share your personal information in the following
          situations:
        </p>
        <ul className={cnPrivacy('list')}>
          <li className={cnPrivacy('item')}>
            Business Transfers. We may share or transfer your information in
            connection with, or during negotiations of, any merger, sale of
            company assets, financing, or acquisition of all or a portion of our
            business to another company.
          </li>
        </ul>
        <h2 className={cnPrivacy('subtitle')}>
          5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
        </h2>
        <p className={cnPrivacy('text')}>
          In Short: We may use cookies and other tracking technologies to
          collect and store your information. <br />
          We may use cookies and similar tracking technologies (like web beacons
          and pixels) to gather information when you interact with our Services.
          Some online tracking technologies help us maintain the security of our
          Services and your account, prevent crashes, fix bugs, save your
          preferences, and assist with basic site functions. <br />
          We also permit third parties and service providers to use online
          tracking technologies on our Services for analytics and advertising,
          including to help manage and display advertisements, to tailor
          advertisements to your interests, or to send abandoned shopping cart
          reminders (depending on your communication preferences). The third
          parties and service providers use their technology to provide
          advertising about products and services tailored to your interests
          which may appear either on our Services or on other websites. <br />
          Specific information about how we use such technologies and how you
          can refuse certain cookies is set out in our Cookie Notice.
        </p>
        <h2 className={cnPrivacy('subtitle')}>
          6. HOW LONG DO WE KEEP YOUR INFORMATION?
        </h2>
        <p className={cnPrivacy('text')}>
          In Short: We keep your information for as long as necessary to fulfil
          the purposes outlined in this Privacy Notice unless otherwise required
          by law. <br />
          We will only keep your personal information for as long as it is
          necessary for the purposes set out in this Privacy Notice, unless a
          longer retention period is required or permitted by law (such as tax,
          accounting, or other legal requirements). No purpose in this notice
          will require us to keep your personal information for longer than the
          period of time in which users have an account with us. <br />
          When we have no ongoing legitimate business need to process your
          personal information, we will either delete or anonymise such
          information, or, if this is not possible (for example, because your
          personal information has been stored in backup archives), then we will
          securely store your personal information and isolate it from any
          further processing until deletion is possible.
        </p>
        <h2 className={cnPrivacy('subtitle')}>
          7. HOW DO WE KEEP YOUR INFORMATION SAFE?
        </h2>
        <p className={cnPrivacy('text')}>
          In Short: We aim to protect your personal information through a system
          of organisational and technical security measures. <br />
          We have implemented appropriate and reasonable technical and
          organisational security measures designed to protect the security of
          any personal information we process. However, despite our safeguards
          and efforts to secure your information, no electronic transmission
          over the Internet or information storage technology can be guaranteed
          to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorised third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information. Although we will do our best to protect your
          personal information, transmission of personal information to and from
          our Services is at your own risk. You should only access the Services
          within a secure environment.
        </p>
        <h2 className={cnPrivacy('subtitle')}>
          8. DO WE COLLECT INFORMATION FROM MINORS?
        </h2>
        <p className={cnPrivacy('text')}>
          In Short: We do not knowingly collect data from or market to children
          under 18 years of age. <br />
          We do not knowingly collect, solicit data from, or market to children
          under 18 years of age, nor do we knowingly sell such personal
          information. By using the Services, you represent that vou are at
          least 18 or that vou are the parent or guardian of such a minor and
          consent to such minor dependent's use of the Services. If we learn
          that personal information from users less than 18 years of age has
          been collected, we will deactivate the account and take reasonable
          measures to promptly delete such data from our records. If vou become
          aware of any data we may have collected from children under age 18,
          please contact us at privacy@exercial.com.
        </p>
        <h2 className={cnPrivacy('subtitle')}>
          9. WHAT ARE YOUR PRIVACY RIGHTS?
        </h2>
        <p className={cnPrivacy('text')}>
          In Short: In some regions, such as the European Economic Area (EEA),
          United Kingdom (UK), and Switzerland, you have rights that allow you
          greater access to and control over your personal information. You may
          review, change, or terminate your account at any time, depending on
          your country, province, or state of residence. <br />
          In some regions (like the EA, UK, and Switzerland), you have certain
          rights under applicable data protection laws. These may include the
          right (i) to request access and obtain a copy of your personal
          information, (ii) to request rectification or erasure; (iii) to
          restrict the processing of your personal information; (iv) if
          applicable, to data portability; and (v) not to be subject to
          automated decision-making. In certain circumstances, you may also have
          the right to object to the processing of your personal information.
          You can make such a request by contacting us by using the contact
          details provided in the section 'HOW CAN YOU CONTACT US ABOUT THIS
          NOTICE?' below. <br />
          We will consider and act upon any request in accordance with
          applicable data protection laws. <br />
          If you are located in the EA or UK and you believe we are unlawfully
          processing your personal information, you also have the right to
          complain to your Member State data protection authority or UK data
          protection authority. <br />
          If you are located in Switzerland, you may contact the Federal Data
          Protection and Information Commissioner. <br />
          Withdrawing your consent: If we are relying on your consent to process
          your personal information, you have the right to withdraw your consent
          at any time. You can withdraw your consent at any time by contacting
          us by using the contact details provided in the section 'HOW CAN YOU
          CONTACT US ABOUT THIS NOTICE?' below. <br />
          However, please note that this will not affect the lawfulness of the
          processing before its withdrawal nor, will it affect the processing of
          your personal information conducted in reliance on lawful processing
          grounds other than consent. <br />
          Account Information <br />
          If you would at any time like to review or change the information in
          your account or terminate your account, you can:
        </p>
        <ul className={cnPrivacy('list')}>
          <li className={cnPrivacy('item')}>
            Log in to your account settings and update your user account.
          </li>
        </ul>
        <p className={cnPrivacy('text')}>
          Upon vour request to terminate your account. we will deactivate or
          delete vour account and information from our active databases.
          However. we mav retain some information in our files to prevent fraud,
          troubleshoot problems, assist with any investigations, enforce our
          legal terms and/or comply with applicable legal requirements. <br />
          Cookies and similar technologies: Most Web browsers are set to accept
          cookies by default. If vou prefer. vou can usually choose to set vour
          browser to remove cookies and to reject cookies. If you choose to
          remove cookies or reject cookies, this could affect certain features
          or services of our Services.
        </p>
        <h2 className={cnPrivacy('subtitle')}>
          10. CONTROLS FOR DO-NOT-TRACK FEATURES
        </h2>
        <p className={cnPrivacy('text')}>
          Most web browsers and some mobile operating systems and mobile
          applications include a Do-Not-Track (DNT') feature or setting you can
          activate to signal your privacy preference not to have data about your
          online browsing activities monitored and collected. At this stage, no
          uniform technology standard for recognising and implementing DNT
          signals has been finalised. As such, we do not currently respond to
          DNT browser signals or any other mechanism that automatically
          communicates your choice not to be tracked online. If a standard for
          online tracking is adopted that we must follow in the future, we will
          inform you about that practice in a revised version of this Privacy
          Notice.
        </p>
        <h2 className={cnPrivacy('subtitle')}>
          11. DO WE MAKE UPDATES TO THIS NOTICE?
        </h2>
        <p className={cnPrivacy('text')}>
          In Short: Yes, we will update this notice as necessary to stay
          compliant with relevant laws. <br />
          We may update this Privacy Notice from time to time. The updated
          version will be indicated by an updated 'Revised' date at the top of
          this Privacy Notice. If we make material changes to this Privacy
          Notice, we may notify you either by prominently posting a notice of
          such changes or by directly sending you a notification. We encourage
          you to review this Privacy Notice frequently to be informed of how we
          are protecting your information.
        </p>
        <h2 className={cnPrivacy('subtitle')}>
          12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
        </h2>
        <p className={cnPrivacy('text')}>
          If you have questions or comments about this notice, you may email us
          at privacy@exercial.com or contact us by post at: <br />
          EXERCIAL, s.r.o.
        </p>
        <h2 className={cnPrivacy('subtitle')}>
          13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
          YOU?
        </h2>
        <p className={cnPrivacy('text')}>
          Based on the applicable laws of your country, you may have the right
          to request access to the personal information we collect from you,
          details about how we have processed it, correct inaccuracies, or
          delete your personal information. You may also have the right to
          withdraw your consent to our processing of your personal information.
          These rights may be limited in some circumstances by applicable law.
          To request to review, update, or delete your personal information,
          please fill out and submit a data subiect access request.
        </p>
        <Footer isHome />
      </div>
    </div>
  )
}
